import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Configure axios base URL and interceptors
  useEffect(() => {
    // Set the base URL for all axios requests
    axios.defaults.baseURL = 'https://dental.easyaidev.com';
    
    // Add a request interceptor to ensure the base URL is always used
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Ensure we're always using the correct base URL
        if (!config.url.startsWith('http')) {
          config.baseURL = 'https://dental.easyaidev.com';
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token validity
      axios.get('/api/auth/verify')
        .then(response => {
          setUser(response.data.user);
          // If user is a doctor, fetch doctor details including profile image
          if (response.data.user.role === 'doctor') {
            fetchDoctorDetails(response.data.user.id);
          }
        })
        .catch(error => {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch doctor details if user is a doctor
  const fetchDoctorDetails = async (doctorId) => {
    try {
      const response = await axios.get(`/api/settings/doctors/${doctorId}`);
      if (response.data && response.data.profile_image) {
        // Update user state with profile image
        setUser(prev => ({
          ...prev,
          profile_image: response.data.profile_image
        }));
      }
    } catch (error) {
      console.error('Error fetching doctor details:', error);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login for:', email);
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      const { token, user } = response.data;
      console.log('✅ Login successful:', user.name);

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // If user is a doctor, fetch their profile image
      if (user.role === 'doctor') {
        try {
          const doctorResponse = await axios.get(`/api/settings/doctors/${user.id}`);
          if (doctorResponse.data && doctorResponse.data.profile_image) {
            user.profile_image = doctorResponse.data.profile_image;
          }
        } catch (error) {
          console.error('Error fetching doctor profile:', error);
        }
      }

      setUser(user);
      return { success: true };
    } catch (error) {
      console.error('❌ Login failed:', error);
      let errorMessage = 'Login failed';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Unable to connect to server. Please check if the server is running.';
      } else if (error.code === 'ECONNREFUSED') {
        errorMessage = 'Connection refused. Please ensure the server is running on port 3001.';
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Redirect to login page
    navigate('/login');
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};